import {
	Box,
	Checkbox,
	Button,
	Heading,
	IconButton,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Stack,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Text,
	Tooltip
} from "@chakra-ui/react";
import {useLazyQuery} from "@apollo/client";
import {FiSettings} from "react-icons/fi";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {AppContext, AppContextI} from "../context/ContextWrapper";
import HelpTooltip from "../help/HelpTooltip";
import {compareVersions} from "../common/compareVersions";
import AreaAudioStorageSummary from "./AreaAudioStorageSummary";
import {getAreaStatisticsQuery} from "./areaQueries";
import {
	AREA_GROUP_MODE_AUTO_BY_SPOTS,
	AREA_GROUP_MODE_FIXED,
	AREA_GROUP_MODE_USER_CREATED,
	AREA_LANGUAGE_DOWNLOAD_ALL,
	AREA_LANGUAGE_DOWNLOAD_DEFAULT_ONLY,
	AREA_LANGUAGE_DOWNLOAD_USER_CHOOSE,
	AreaLanguageDownloadMode,
	AREA_SHOW_SOUND_INFO_MAIN,
	AREA_SHOW_SOUND_INFO_MAIN_AND_AMBIENT,
	AREA_SHOW_SOUND_INFO_NONE,
	AREA_SPOT_TRIGGER_MODE_AUTOMATIC,
	AREA_SPOT_TRIGGER_MODE_ON_DEMAND,
	AREA_FLOW_SEQUENTIAL,
	AREA_FLOW_SPATIAL,
	AREA_DISCOVER_MODE_PLAYING,
	Area,
	AreaDiscoverMode,
	AreaFlowMode,
	AreaStatistics,
	AudioMode,
	AreaGroupMode,
	AreaLanguageOption,
	AreaShowSoundInfoMode,
	BLE_SCAN_MODE_DONGLE_OR_PHONE,
	BleScanMode,
	LatLong,
	resolveAreaAutoPlay,
	resolveAreaDiscoverMode
} from "../types";
import {UserContext, UserContextI} from "../../UserContext";
import {isRootAdminRole} from "../../auth/types";
import AreaGeneralTab from "./settings/AreaGeneralTab";
import VisibilitySettingsTab from "./settings/VisibilitySettingsTab";
import GeneralSettingsTab from "./settings/GeneralSettingsTab";
import SoundSettingsTab from "./settings/SoundSettingsTab";
import {useConfirmDialog} from "../common/ConfirmDialogProvider";
import {findWavCarriers, WAV_WARNING_REASONS} from "../soundfiles/wavWarning";
import LanguageSettingsTab from "./settings/LanguageSettingsTab";
import TimingsSettingsTab from "./settings/TimingsSettingsTab";
import {
	BASE_LANGUAGE_OPTIONS,
	buildLanguageCatalog,
	FALLBACK_LANGUAGE,
	migrateZonesForBoundariesSetting,
	normalizeAreaGroupModes,
	normalizeLanguageCode,
	resolveBleScanMode,
	resolveSuggestedFlagUrl
} from "./settings/areaSettingsUtils";

type Props = {
	canUseExtendedLanguages?: boolean;
	canUseSequentialFlow?: boolean;
};

const AreaSettingsManager = ({canUseExtendedLanguages = false, canUseSequentialFlow = false}: Props) => {
	const appContext: AppContextI = useContext<AppContextI>(AppContext);
	const userContext: UserContextI = useContext<UserContextI>(UserContext);
	const {confirm} = useConfirmDialog();
	const area = appContext.getArea();
	const isRootAdmin = isRootAdminRole(userContext.getUser()?.role);
	const orgAppVersion = appContext.getOrganizationAppVersion() || "3.15";
	const isAudioModeAvailable = compareVersions(orgAppVersion, "6.1") >= 0;
	const isTimingsAvailable = compareVersions(orgAppVersion, "6.2") >= 0;
	const [isOpen, setOpen] = useState<boolean>(false);
	const [areaName, setAreaName] = useState<string>("");
	const [areaPublic, setAreaPublic] = useState<boolean>(false);
	const [publicAccessCode, setPublicAccessCode] = useState<string>("");
	const [areaSummary, setAreaSummary] = useState<string>("");
	const [areaDescription, setAreaDescription] = useState<string>("");
	const [areaCoverImageUrl, setAreaCoverImageUrl] = useState<string | undefined>(undefined);
	const [areaCreator, setAreaCreator] = useState<string>("");
	const [altSyncServerAddress, setAltSyncServerAddress] = useState<string>("");
	const [configuredLanguages, setConfiguredLanguages] = useState<AreaLanguageOption[]>([]);
	const [defaultLanguage, setDefaultLanguage] = useState<string>(FALLBACK_LANGUAGE);
	const [languageDownloadMode, setLanguageDownloadMode] = useState<AreaLanguageDownloadMode>(AREA_LANGUAGE_DOWNLOAD_ALL);
	const [languageToAdd, setLanguageToAdd] = useState<string>("");
	const [showMap, setShowMap] = useState<boolean>(false);
	const [developerMode, setDeveloperMode] = useState<boolean>(false);
	const [showSoundInfo, setShowSoundInfo] = useState<AreaShowSoundInfoMode>(AREA_SHOW_SOUND_INFO_MAIN_AND_AMBIENT);
	const [showTransport, setShowTransport] = useState<boolean>(false);
	const [groupModes, setGroupModes] = useState<AreaGroupMode[]>([]);
	const [groupModeAutoWindow, setGroupModeAutoWindow] = useState<string>("30");
	const [setDefaultVolumeOnReset, setSetDefaultVolumeOnReset] = useState<boolean>(false);
	const [appOverridesSystemVolume, setAppOverridesSystemVolume] = useState<boolean>(false);
	const [disableVolumeButtons, setDisableVolumeButtons] = useState<boolean>(false);
	const [defaultVolume, setDefaultVolume] = useState<number>(70);
	const [pendingDefaultVolume, setPendingDefaultVolume] = useState<number>(70);
	const [bleScanMode, setBleScanMode] = useState<BleScanMode>(BLE_SCAN_MODE_DONGLE_OR_PHONE);
	const [autoPlay, setAutoPlay] = useState<boolean>(true);
	const [discoverMode, setDiscoverMode] = useState<AreaDiscoverMode>(AREA_DISCOVER_MODE_PLAYING);
	const [useBoundariesZone, setUseBoundariesZone] = useState<boolean>(false);
	const [audioMode, setAudioMode] = useState<AudioMode | undefined>(undefined);
	const [flowMode, setFlowMode] = useState<AreaFlowMode>(AREA_FLOW_SPATIAL);
	const [downloadFrom, setDownloadFrom] = useState<string | undefined>(undefined);
	const [downloadTo, setDownloadTo] = useState<string | undefined>(undefined);
	const [playFrom, setPlayFrom] = useState<string | undefined>(undefined);
	const [playTo, setPlayTo] = useState<string | undefined>(undefined);
	const [areaPosition, setAreaPosition] = useState<LatLong | undefined>(undefined);
	const [loadAreaStatistics, {data: areaStatisticsData}] = useLazyQuery(getAreaStatisticsQuery, {
		fetchPolicy: "network-only",
	});
	const fullLanguageCatalog = useMemo(() => buildLanguageCatalog(), []);
	const currentAreaStatistics = useMemo(() => {
		const entries: AreaStatistics[] = areaStatisticsData?.getAreaStatistics || [];
		return entries.find((entry) => entry?.areaId === area?.id);
	}, [area?.id, areaStatisticsData]);
	const fullLanguageCatalogByCode = useMemo(() => {
		const map = new Map<string, AreaLanguageOption>();
		fullLanguageCatalog.forEach((option) => map.set(option.code, option));
		return map;
	}, [fullLanguageCatalog]);
	const candidateLanguageCatalog = useMemo(() => {
		if (canUseExtendedLanguages) {
			return fullLanguageCatalog;
		}
		const baseCodes = new Set(BASE_LANGUAGE_OPTIONS.map((language) => normalizeLanguageCode(language.code)));
		return fullLanguageCatalog.filter((language) => baseCodes.has(language.code));
	}, [canUseExtendedLanguages, fullLanguageCatalog]);

	const availableLanguageCandidates = useMemo(() => {
		const selectedCodes = new Set(configuredLanguages.map((language) => normalizeLanguageCode(language.code)));
		return candidateLanguageCatalog.filter((language) => !selectedCodes.has(language.code));
	}, [configuredLanguages, candidateLanguageCatalog]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const selectedFromArea = (area?.languages?.available || [])
			.map((language) => {
				const normalizedCode = normalizeLanguageCode(language.code);
				const catalogLanguage = fullLanguageCatalogByCode.get(normalizedCode);
				return {
					code: normalizedCode,
					label: (language.label || catalogLanguage?.label || normalizedCode).trim(),
					flagUrl: (language.flagUrl || catalogLanguage?.flagUrl || "").trim() || undefined
				} as AreaLanguageOption;
			})
			.filter((language) => !!language.code);
		const initialLanguages = selectedFromArea.length > 0
			? selectedFromArea
			: BASE_LANGUAGE_OPTIONS.map((language) => ({
				code: language.code,
				label: language.label,
				flagUrl: language.flagUrl
			}));
		setConfiguredLanguages(initialLanguages);
		const areaDefault = normalizeLanguageCode(area?.languages?.default);
		const nextDefault = initialLanguages.some((language) => language.code === areaDefault)
			? areaDefault
			: (initialLanguages[0]?.code || FALLBACK_LANGUAGE);
		setAreaName(area?.name || "");
		setAreaPublic(area?.public === true);
		setPublicAccessCode(area?.publicAccessCode || "");
		setAreaSummary(area?.summary || "");
		setAreaDescription(area?.description || "");
		setAreaCoverImageUrl(area?.coverImageUrl || undefined);
		setAreaCreator(area?.creator || "");
		setAltSyncServerAddress(area?.altSyncServerAddress || "");
		setDefaultLanguage(nextDefault);
		setLanguageDownloadMode(
			area?.languages?.downloadMode === AREA_LANGUAGE_DOWNLOAD_DEFAULT_ONLY
				? AREA_LANGUAGE_DOWNLOAD_DEFAULT_ONLY
				: area?.languages?.downloadMode === AREA_LANGUAGE_DOWNLOAD_USER_CHOOSE
					? AREA_LANGUAGE_DOWNLOAD_USER_CHOOSE
					: AREA_LANGUAGE_DOWNLOAD_ALL
		);
		setShowMap(area?.showMap !== undefined ? area.showMap : false);
		setDeveloperMode(area?.developerMode === true);
		setShowSoundInfo(
			area?.showSoundInfo === AREA_SHOW_SOUND_INFO_MAIN
				|| area?.showSoundInfo === AREA_SHOW_SOUND_INFO_MAIN_AND_AMBIENT
				|| area?.showSoundInfo === AREA_SHOW_SOUND_INFO_NONE
				? area.showSoundInfo
				: AREA_SHOW_SOUND_INFO_MAIN_AND_AMBIENT
		);
		setShowTransport(area?.showTransport === true);
		setGroupModes(normalizeAreaGroupModes(area?.groupMode));
		setGroupModeAutoWindow(String(
			typeof area?.groupModeAutoWindow === "number" && Number.isFinite(area.groupModeAutoWindow) && area.groupModeAutoWindow > 0
				? area.groupModeAutoWindow
				: 30
		));
		setSetDefaultVolumeOnReset(area?.setDefaultVolumeOnReset === true);
		const appOverridesSystemVolumeValue = area?.appOverridesSystemVolume;
		setAppOverridesSystemVolume(
			appOverridesSystemVolumeValue !== undefined
				? appOverridesSystemVolumeValue
				: true
		);
		setDisableVolumeButtons(area?.disableVolumeButtons !== false);
		const volume = typeof area?.defaultVolume === "number" ? area.defaultVolume : 70;
		setDefaultVolume(volume);
		setPendingDefaultVolume(volume);
		setBleScanMode(resolveBleScanMode(area?.bleScanMode));
		setAutoPlay(resolveAreaAutoPlay(area));
		setDiscoverMode(resolveAreaDiscoverMode(area));
		setAudioMode(
			area?.audioMode === "stream" || area?.audioMode === "download" ? area.audioMode : undefined
		);
		setFlowMode(area?.flowMode === AREA_FLOW_SEQUENTIAL ? AREA_FLOW_SEQUENTIAL : AREA_FLOW_SPATIAL);
		const initialPositionLat = typeof area?.position?.lat === "number" ? area.position.lat : undefined;
		const initialPositionLng = typeof area?.position?.lng === "number" ? area.position.lng : undefined;
		setAreaPosition(
			initialPositionLat !== undefined && initialPositionLng !== undefined
				? {lat: initialPositionLat, lng: initialPositionLng}
				: undefined
		);
		setDownloadFrom(typeof area?.downloadFrom === "string" && area.downloadFrom ? area.downloadFrom : undefined);
		setDownloadTo(typeof area?.downloadTo === "string" && area.downloadTo ? area.downloadTo : undefined);
		setPlayFrom(typeof area?.playFrom === "string" && area.playFrom ? area.playFrom : undefined);
		setPlayTo(typeof area?.playTo === "string" && area.playTo ? area.playTo : undefined);
		setUseBoundariesZone(area?.useBoundariesZone === true);
		const selectedCodes = new Set(initialLanguages.map((language) => language.code));
		const initialCandidate = candidateLanguageCatalog.find((language) => !selectedCodes.has(language.code))?.code || "";
		setLanguageToAdd(initialCandidate);
	}, [isOpen, area, fullLanguageCatalogByCode, candidateLanguageCatalog]);

	useEffect(() => {
		if (!availableLanguageCandidates.some((option) => option.code === languageToAdd)) {
			setLanguageToAdd(availableLanguageCandidates[0]?.code || "");
		}
	}, [availableLanguageCandidates, languageToAdd]);

	useEffect(() => {
		if (!isOpen || !area?.id) {
			return;
		}
		loadAreaStatistics({
			variables: {
				areaIds: [area.id],
			},
		});
	}, [area?.id, isOpen, loadAreaStatistics]);

	const handleAddLanguage = () => {
		if (!languageToAdd) {
			return;
		}
		const languageOption = candidateLanguageCatalog.find((language) => language.code === languageToAdd);
		if (!languageOption) {
			return;
		}
		if (configuredLanguages.some((language) => language.code === languageOption.code)) {
			return;
		}
		const nextLanguages = [...configuredLanguages, {
			code: languageOption.code,
			label: languageOption.label,
			flagUrl: languageOption.flagUrl
		}];
		setConfiguredLanguages(nextLanguages);
		if (nextLanguages.length === 1) {
			setDefaultLanguage(nextLanguages[0].code);
		}
	};

	const handleRemoveLanguage = (code: string) => {
		const nextLanguages = configuredLanguages.filter((language) => language.code !== code);
		setConfiguredLanguages(nextLanguages);
		if (nextLanguages.length === 0) {
			setDefaultLanguage(FALLBACK_LANGUAGE);
			return;
		}
		if (!nextLanguages.some((language) => language.code === defaultLanguage)) {
			setDefaultLanguage(nextLanguages[0].code);
		}
	};

	const handleLanguageLabelChange = (code: string, label: string) => {
		setConfiguredLanguages((previous) => previous.map((language) => {
			if (language.code !== code) {
				return language;
			}
			return {
				...language,
				label: label
			};
		}));
	};

	const handleLanguageFlagChange = (code: string, flagUrl: string) => {
		setConfiguredLanguages((previous) => previous.map((language) => {
			if (language.code !== code) {
				return language;
			}
			const nextFlag = flagUrl.trim();
			return {
				...language,
				flagUrl: nextFlag || undefined
			};
		}));
	};

	const handleUseSuggestedFlag = (code: string) => {
		const suggestedFlag = resolveSuggestedFlagUrl(code);
		if (!suggestedFlag) {
			return;
		}
		setConfiguredLanguages((previous) => previous.map((language) => {
			if (language.code !== code) {
				return language;
			}
			return {
				...language,
				flagUrl: suggestedFlag
			};
		}));
	};

	const handleDefaultChange = (value: string) => {
		const normalized = normalizeLanguageCode(value);
		if (!configuredLanguages.some((language) => language.code === normalized)) {
			return;
		}
		setDefaultLanguage(normalized);
	};

	const handleGroupModeToggle = (mode: AreaGroupMode, checked: boolean) => {
		setGroupModes((previous) => {
			if (checked) {
				return previous.includes(mode) ? previous : [...previous, mode];
			}
			return previous.filter((value) => value !== mode);
		});
	};

	const handleGroupModeAutoWindowChange = (value: string) => {
		if (value === "") {
			setGroupModeAutoWindow("");
			return;
		}
		if (!/^\d+$/.test(value)) {
			return;
		}
		setGroupModeAutoWindow(value);
	};

	const handlePublicAccessCodeChange = (value: string) => {
		if (!/^\d*$/.test(value)) {
			return;
		}
		setPublicAccessCode(value.slice(0, 6));
	};

	const handleCoverImageChange = (value: string | undefined) => {
		const nextValue = value && value.trim() ? value.trim() : undefined;
		setAreaCoverImageUrl(nextValue);
		if (area) {
			appContext.updateArea(area, {...area, coverImageUrl: nextValue});
		}
	};

	const handleSave = () => {
		if (!area) {
			return;
		}
		const sanitizedLanguages = configuredLanguages
			.map((language) => {
				const normalizedCode = normalizeLanguageCode(language.code);
				if (!normalizedCode) {
					return undefined;
				}
				const catalogLanguage = fullLanguageCatalogByCode.get(normalizedCode);
				const label = language.label.trim() || catalogLanguage?.label || normalizedCode;
				const flagUrl = language.flagUrl?.trim() || catalogLanguage?.flagUrl;
				return {
					code: normalizedCode,
					label: label,
					flagUrl: flagUrl || undefined
				} as AreaLanguageOption;
			})
			.filter((language): language is AreaLanguageOption => !!language);
		const nextDefault = sanitizedLanguages.length === 0
			? FALLBACK_LANGUAGE
			: (sanitizedLanguages.some((language) => language.code === defaultLanguage)
				? defaultLanguage
				: sanitizedLanguages[0].code);
		const sortedLanguages = [...sanitizedLanguages].sort((a, b) => {
			if (a.code === nextDefault && b.code !== nextDefault) {
				return -1;
			}
			if (a.code !== nextDefault && b.code === nextDefault) {
				return 1;
			}
			return a.code.localeCompare(b.code);
		});
		const parsedGroupModeAutoWindow = Number.parseInt(groupModeAutoWindow, 10);
		const normalizedGroupModeAutoWindow = Number.isFinite(parsedGroupModeAutoWindow) && parsedGroupModeAutoWindow > 0
			? parsedGroupModeAutoWindow
			: 30;
		const normalizedPublicAccessCode = publicAccessCode.trim();
		const nextArea = {
			...area,
			name: areaName.trim() || area.name,
			public: areaPublic,
			publicAccessCode: /^\d{6}$/.test(normalizedPublicAccessCode)
				? normalizedPublicAccessCode
				: undefined,
			summary: areaSummary.trim() || undefined,
			description: areaDescription.trim() || undefined,
			coverImageUrl: areaCoverImageUrl?.trim() || undefined,
			creator: areaCreator.trim() || undefined,
			altSyncServerAddress: altSyncServerAddress.trim() || undefined,
			zones: migrateZonesForBoundariesSetting(area.zones, useBoundariesZone),
			languages: {
				default: nextDefault,
				available: sortedLanguages,
				downloadMode: languageDownloadMode
			},
			showMap: showMap,
			developerMode: developerMode,
			showSoundInfo: showSoundInfo,
			showTransport: showTransport,
			groupMode: groupModes,
			groupModeAutoWindow: normalizedGroupModeAutoWindow,
			setDefaultVolumeOnReset: setDefaultVolumeOnReset,
			appOverridesSystemVolume: appOverridesSystemVolume,
			disableVolumeButtons: disableVolumeButtons,
			defaultVolume: defaultVolume,
			bleScanMode: bleScanMode,
			spotTriggerMode: autoPlay ? AREA_SPOT_TRIGGER_MODE_AUTOMATIC : AREA_SPOT_TRIGGER_MODE_ON_DEMAND,
			autoPlay: autoPlay,
			discoverMode: discoverMode,
			useBoundariesZone: useBoundariesZone,
			audioMode: audioMode,
			flowMode: flowMode,
			downloadFrom: downloadFrom,
			downloadTo: downloadTo,
			playFrom: playFrom,
			playTo: playTo,
			position: areaPosition && Number.isFinite(areaPosition.lat) && Number.isFinite(areaPosition.lng)
				? areaPosition
				: undefined
		} as Area;
		console.info("[area-settings] save", {
			areaId: area.id,
			modifiedAt: area.modifiedAt,
			name: nextArea.name,
			developerMode: nextArea.developerMode,
			bleScanMode: nextArea.bleScanMode,
			groupMode: nextArea.groupMode,
			groupModeAutoWindow: nextArea.groupModeAutoWindow,
		});
		appContext.updateArea(area, nextArea);
		setOpen(false);
	};

	const canEdit = appContext.isDraft();
	const showTransportForcedOff = !autoPlay;
	const hasAutomaticGroupMode = groupModes.includes(AREA_GROUP_MODE_AUTO_BY_SPOTS);
	const nonFixedGroupModesDisabled = !isRootAdmin;
	const publicAccessCodeError = publicAccessCode.trim().length > 0 && !/^\d{6}$/.test(publicAccessCode.trim())
		? "Use exactly 6 digits."
		: undefined;

	const timingsError = useMemo(() => {
		const downloadFromTime = downloadFrom ? new Date(downloadFrom).getTime() : undefined;
		const downloadToTime = downloadTo ? new Date(downloadTo).getTime() : undefined;
		const playFromTime = playFrom ? new Date(playFrom).getTime() : undefined;
		const playToTime = playTo ? new Date(playTo).getTime() : undefined;
		if (downloadFromTime !== undefined && downloadToTime !== undefined && downloadFromTime > downloadToTime) {
			return "Download \"From\" must be before \"To\".";
		}
		if (playFromTime !== undefined && playToTime !== undefined && playFromTime > playToTime) {
			return "Play \"From\" must be before \"To\".";
		}
		if (downloadFromTime !== undefined && playFromTime !== undefined && playFromTime < downloadFromTime) {
			return "Play window must start at or after the download window.";
		}
		return undefined;
	}, [downloadFrom, downloadTo, playFrom, playTo]);

	const handleAudioModeChange = async (next: AudioMode) => {
		if (next === "stream" && audioMode !== "stream" && area) {
			const carriers = findWavCarriers(area, appContext.getSpots() || []);
			if (carriers.length > 0) {
				const sample = carriers.slice(0, 6).map((c) => `${c.kind === "spot" ? "Spot" : "Zone"}: ${c.label}`);
				const overflow = carriers.length - sample.length;
				const approved = await confirm({
					title: "Switch to streaming?",
					body: (
						<Box>
							<Text mb={3}>
								This space contains {carriers.length} item{carriers.length === 1 ? "" : "s"} with WAV sound files. WAV is uncompressed and a poor fit for streaming:
							</Text>
							<Stack spacing={1} fontSize={"sm"} color={"gray.700"} mb={3}>
								{WAV_WARNING_REASONS.map((reason) => (
									<Text key={reason}>{`• ${reason}`}</Text>
								))}
							</Stack>
							<Text mb={2} fontWeight={"semibold"}>Affected items:</Text>
							<Stack spacing={0.5} fontSize={"sm"} color={"gray.700"}>
								{sample.map((line) => (
									<Text key={line}>{`• ${line}`}</Text>
								))}
								{overflow > 0 ? (
									<Text color={"gray.500"}>{`• …and ${overflow} more`}</Text>
								) : null}
							</Stack>
							<Text mt={3} fontSize={"sm"} color={"gray.600"}>
								Re-encode them to MP3 or AAC for the best streaming experience.
							</Text>
						</Box>
					),
					confirmText: "Switch to streaming",
					cancelText: "Cancel"
				});
				if (!approved) {
					return;
				}
			}
		}
		setAudioMode(next);
	};

	const handleFlowModeChange = async (next: AreaFlowMode) => {
		if (next === flowMode) {
			return;
		}
		const nextLabel = next === AREA_FLOW_SEQUENTIAL ? "Sequential (Cue list)" : "Spatial (Spot list)";
		const otherLabel = next === AREA_FLOW_SEQUENTIAL ? "Spatial (Spot list)" : "Sequential (Cue list)";
		const approved = await confirm({
			title: `Switch to ${nextLabel}?`,
			body: (
				<Box>
					<Text mb={2}>
						{`This switches the space to ${nextLabel}. The ${otherLabel} editor will no longer be available.`}
					</Text>
					<Text fontSize={"sm"} color={"gray.600"}>
						Existing rules are kept but may not all apply in the new flow.
					</Text>
				</Box>
			),
			confirmText: `Switch to ${nextLabel}`,
			cancelText: "Cancel"
		});
		if (!approved) {
			return;
		}
		setFlowMode(next);
	};

	return (
		<>
			<Tooltip label={"Space settings"} hasArrow>
				<IconButton
					aria-label={"Space settings"}
					icon={<FiSettings/>}
					variant={"ghost"}
					size={"sm"}
					onClick={() => setOpen(true)}
				/>
			</Tooltip>
			<Modal isOpen={isOpen} onClose={() => setOpen(false)} isCentered size={"6xl"} blockScrollOnMount={false}>
				<ModalOverlay/>
				<ModalContent maxW={"90vw"} height={"90vh"}>
					<ModalHeader>Space settings</ModalHeader>
					<ModalCloseButton/>
					<ModalBody height={"100%"} overflowY={"auto"}>
						<Tabs variant={"enclosed"} colorScheme={"blue"} isLazy>
							<TabList>
								<Tab>General</Tab>
								<Tab>Visibility</Tab>
								<Tab>Settings</Tab>
								<Tab>Sound</Tab>
								<Tab>Language</Tab>
								<Tab>App UI</Tab>
								<Tab>Groups</Tab>
								{isTimingsAvailable ? <Tab>Timings</Tab> : null}
								<Tab>Advanced</Tab>
								<Tab>Statistics</Tab>
							</TabList>
							<TabPanels>
								<TabPanel px={0} pt={4}>
									<AreaGeneralTab
										canEdit={canEdit}
										areaId={area?.id}
										name={areaName}
										summary={areaSummary}
										description={areaDescription}
										coverImageUrl={areaCoverImageUrl}
										creator={areaCreator}
										onNameChange={setAreaName}
										onSummaryChange={setAreaSummary}
										onDescriptionChange={setAreaDescription}
										onCoverImageChange={handleCoverImageChange}
										onCreatorChange={setAreaCreator}
									/>
								</TabPanel>
								<TabPanel px={0} pt={4}>
									<VisibilitySettingsTab
										canEdit={canEdit}
										isPublic={areaPublic}
										publicAccessCode={publicAccessCode}
										publicAccessCodeError={publicAccessCodeError}
										position={areaPosition}
										spots={appContext.getSpots() || []}
										onPublicChange={setAreaPublic}
										onPublicAccessCodeChange={handlePublicAccessCodeChange}
										onPositionChange={setAreaPosition}
									/>
								</TabPanel>
								<TabPanel px={0} pt={4}>
									<GeneralSettingsTab
										canEdit={canEdit}
										bleScanMode={bleScanMode}
										autoPlay={autoPlay}
										discoverMode={discoverMode}
										useBoundariesZone={useBoundariesZone}
										flowMode={flowMode}
										canChooseSequentialFlow={canUseSequentialFlow}
										onBleScanModeChange={setBleScanMode}
										onAutoPlayChange={setAutoPlay}
										onDiscoverModeChange={setDiscoverMode}
										onUseBoundariesZoneChange={setUseBoundariesZone}
										onFlowModeChange={handleFlowModeChange}
									/>
								</TabPanel>
								<TabPanel px={0} pt={4}>
									<SoundSettingsTab
										canEdit={canEdit}
										appOverridesSystemVolume={appOverridesSystemVolume}
										disableVolumeButtons={disableVolumeButtons}
										setDefaultVolumeOnReset={setDefaultVolumeOnReset}
										pendingDefaultVolume={pendingDefaultVolume}
										audioMode={audioMode}
										isAudioModeAvailable={isAudioModeAvailable}
										onAppOverridesSystemVolumeChange={setAppOverridesSystemVolume}
										onDisableVolumeButtonsChange={setDisableVolumeButtons}
										onSetDefaultVolumeOnResetChange={setSetDefaultVolumeOnReset}
										onPendingDefaultVolumeChange={setPendingDefaultVolume}
										onDefaultVolumeCommit={setDefaultVolume}
										onAudioModeChange={handleAudioModeChange}
									/>
								</TabPanel>
								<TabPanel px={0} pt={4}>
									<LanguageSettingsTab
										canEdit={canEdit}
										canUseExtendedLanguages={canUseExtendedLanguages}
										defaultLanguage={defaultLanguage}
										languageDownloadMode={languageDownloadMode}
										languageToAdd={languageToAdd}
										configuredLanguages={configuredLanguages}
										availableLanguageCandidates={availableLanguageCandidates}
										onLanguageDownloadModeChange={setLanguageDownloadMode}
										onLanguageToAddChange={setLanguageToAdd}
										onAddLanguage={handleAddLanguage}
										onDefaultChange={handleDefaultChange}
										onRemoveLanguage={handleRemoveLanguage}
										onLanguageLabelChange={handleLanguageLabelChange}
										onLanguageFlagChange={handleLanguageFlagChange}
										onUseSuggestedFlag={handleUseSuggestedFlag}
										getSuggestedFlagUrl={resolveSuggestedFlagUrl}
									/>
								</TabPanel>
								<TabPanel px={0} pt={4}>
									<Stack spacing={6}>
										<Box>
											<Heading size={"sm"} mb={2}>Navigation</Heading>
											<Stack spacing={2}>
												<Checkbox
													isChecked={showMap}
													onChange={(event) => setShowMap(event.target.checked)}
													isDisabled={!canEdit}
												>
													Show map
												</Checkbox>
												<Checkbox
													isChecked={developerMode}
													onChange={(event) => setDeveloperMode(event.target.checked)}
													isDisabled={!canEdit}
												>
													Developer mode
												</Checkbox>
											</Stack>
										</Box>
										<Box>
											<Heading size={"sm"} mb={2}>Active sound info</Heading>
											<Stack spacing={2}>
												<Select
													value={showSoundInfo}
													onChange={(event) => setShowSoundInfo(event.target.value as AreaShowSoundInfoMode)}
													isDisabled={!canEdit}
													maxW={"320px"}
												>
													<option value={AREA_SHOW_SOUND_INFO_NONE}>None</option>
													<option value={AREA_SHOW_SOUND_INFO_MAIN}>Main sound</option>
													<option value={AREA_SHOW_SOUND_INFO_MAIN_AND_AMBIENT}>Main and ambient sound</option>
												</Select>
											</Stack>
										</Box>
										<Box>
											<Heading size={"sm"} mb={2}>Rehearsal</Heading>
											<Stack spacing={2}>
												{showTransportForcedOff ? (
													<Checkbox
														isChecked={false}
														isDisabled={true}
													>
														Show transport bar
													</Checkbox>
												) : (
													<Checkbox
														isChecked={showTransport}
														onChange={(event) => setShowTransport(event.target.checked)}
														isDisabled={!canEdit}
													>
														Show transport bar
													</Checkbox>
												)}
											</Stack>
										</Box>
										{!canEdit ? (
											<Text fontSize={"sm"} color={"orange.600"}>
												Area settings can only be updated while editing a draft.
											</Text>
										) : null}
									</Stack>
								</TabPanel>
								<TabPanel px={0} pt={4}>
									<Stack spacing={6}>
										<Box>
											<Heading size={"sm"} mb={2}>Groups</Heading>
											<Stack spacing={3}>
												<Checkbox
													isChecked={groupModes.includes(AREA_GROUP_MODE_FIXED)}
													onChange={(event) => handleGroupModeToggle(AREA_GROUP_MODE_FIXED, event.target.checked)}
													isDisabled={!canEdit}
												>
													Fixed groups
													<HelpTooltip
														content={"Use predefined fixed groups labeled by colors for easy recognition"}
														placement={"right"}
														icon
														iconAriaLabel={"Fixed groups info"}
													/>
												</Checkbox>
												<Checkbox
													isChecked={groupModes.includes(AREA_GROUP_MODE_USER_CREATED)}
													onChange={(event) => handleGroupModeToggle(AREA_GROUP_MODE_USER_CREATED, event.target.checked)}
													isDisabled={!canEdit || nonFixedGroupModesDisabled}
												>
													User groups
													<HelpTooltip
														content={nonFixedGroupModesDisabled
															? "Groups are temporarily available to root admins only."
															: "Allow visitors to create groups manually and let others join by scanning a QR code. Group members hear the same audio in sync (tight sync requires the USB sync dongle)."}
														placement={"right"}
														icon
														iconAriaLabel={"User created groups info"}
													/>
												</Checkbox>
											</Stack>
										</Box>
										{!canEdit ? (
											<Text fontSize={"sm"} color={"orange.600"}>
												Area settings can only be updated while editing a draft.
											</Text>
										) : null}
									</Stack>
								</TabPanel>
								{isTimingsAvailable ? (
									<TabPanel px={0} pt={4}>
										<TimingsSettingsTab
											canEdit={canEdit}
											downloadFrom={downloadFrom}
											downloadTo={downloadTo}
											playFrom={playFrom}
											playTo={playTo}
											error={timingsError}
											onDownloadFromChange={setDownloadFrom}
											onDownloadToChange={setDownloadTo}
											onPlayFromChange={setPlayFrom}
											onPlayToChange={setPlayTo}
										/>
									</TabPanel>
								) : null}
								<TabPanel px={0} pt={4}>
									<Stack spacing={6}>
										<Box>
											<Heading size={"sm"} mb={2}>Sychronization</Heading>
											<Stack spacing={2} maxW={"520px"}>
												<Text fontSize={"sm"} fontWeight={"medium"}>
													Alternative sync server address
												</Text>
												<Text fontSize={"sm"} color={"gray.600"}>
													Optional override for the sync server address used by this area.
												</Text>
												<Input
													value={altSyncServerAddress}
													onChange={(event) => setAltSyncServerAddress(event.target.value)}
													placeholder={"example.server.com"}
													isDisabled={!canEdit}
												/>
											</Stack>
										</Box>
										{!canEdit ? (
											<Text fontSize={"sm"} color={"orange.600"}>
												Area settings can only be updated while editing a draft.
											</Text>
										) : null}
									</Stack>
								</TabPanel>
								<TabPanel px={0} pt={4}>
									<Stack spacing={4}>
										<Box>
											<Heading size={"sm"} mb={2}>Audio storage</Heading>
											<AreaAudioStorageSummary
												statistics={currentAreaStatistics}
												showDraftRow={appContext.isDraft()}
											/>
										</Box>
									</Stack>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</ModalBody>
					<ModalFooter>
						<Button variant={"ghost"} onClick={() => setOpen(false)}>Close</Button>
						<Button colorScheme={"blue"} onClick={handleSave} isDisabled={!canEdit || !!publicAccessCodeError || !!timingsError}>
							Save
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

export default AreaSettingsManager;
